import { COUNTRIES } from "common/constants";
import { TableFormList } from "components/formList";
import {
  useCreateForwardingEntityAddressMutation,
  useDeleteForwardingEntityAddressMutation,
  useFetchForwardingEntityAddressesQuery,
  useUpdateForwardingEntityAddressMutation,
} from "features/forwardingEntities/forwardingEntitiesAPI";
import { useFetchBackendConstantsQuery } from "features/home/agencyPortal/homeAPI";
import { useMemo, useState } from "react";

export default function AddressStep({ forwardingEntityId }) {
  const { data: constants } = useFetchBackendConstantsQuery();
  const { data: forwardingEntityAddresses, isLoading } = useFetchForwardingEntityAddressesQuery(
    {
      forwardingEntityId,
    },
    { skip: !forwardingEntityId },
  );
  const [updateForwardingEntityAddress] = useUpdateForwardingEntityAddressMutation();
  const [deleteForwardingEntityAddress] = useDeleteForwardingEntityAddressMutation();
  const [createForwardingEntityAddress] = useCreateForwardingEntityAddressMutation();

  const [selectedCountry, setSelectedCountry] = useState("US");

  const filterOption = (inputValue, option) => {
    const fullOptionText = option.label;
    return fullOptionText?.toLowerCase().includes(inputValue?.toLowerCase());
  };

  const addressTypeOptions = useMemo(() => {
    return constants?.addressTypes?.map(({ value, display }) => ({ value, label: display }));
  }, [constants?.addressTypes]);

  const stateOptions = useMemo(() => {
    if (selectedCountry === "CA") {
      return (
        constants?.canadianProvinces?.map((province) => ({
          label: `${province.display} (${province.value})`,
          value: province.value,
        })) ?? []
      );
    }
    return (
      constants?.states?.map((state) => ({
        label: `${state.display} (${state.value})`,
        value: state.value,
      })) ?? []
    );
  }, [selectedCountry, constants?.states, constants?.canadianProvinces]);

  const isStateSelect = selectedCountry === "US" || selectedCountry === "CA";

  const columns = useMemo(
    () => [
      {
        dataIndex: "type",
        editable: true,
        inputType: "select",
        title: "Type",
        options: addressTypeOptions,
        render: (value) => addressTypeOptions?.find((opt) => opt.value === value)?.label ?? value,
      },
      {
        dataIndex: "country",
        editable: true,
        inputType: "select",
        title: "Country",
        options: COUNTRIES,
        rules: [
          {
            required: true,
            message: "Select country",
          },
        ],
        inputProps: {
          filterOption,
          showSearch: true,
        },
        onChange: (value) => setSelectedCountry(value),
      },
      {
        dataIndex: "address1",
        editable: true,
        inputType: "text",
        title: "Address 1",
        rules: [
          {
            required: true,
            message: "Enter Address 1",
          },
        ],
      },
      {
        dataIndex: "address2",
        editable: true,
        inputType: "text",
        title: "Address 2",
      },
      {
        dataIndex: "city",
        editable: true,
        inputType: "text",
        title: "City",
        rules: [
          {
            required: true,
            message: "Enter city",
          },
        ],
      },
      {
        dataIndex: "state",
        editable: true,
        inputType: isStateSelect ? "select" : "text",
        rules: [
          {
            required: true,
            message: isStateSelect ? "Select state/province" : "Enter state/province",
          },
        ],
        options: isStateSelect ? stateOptions : undefined,
        inputProps: isStateSelect
          ? {
              filterOption,
              showSearch: true,
            }
          : undefined,
        title: "State/Province",
        render: (value, record) => {
          const match =
            constants?.states?.find((s) => s.value === value) ??
            constants?.canadianProvinces?.find((p) => p.value === value);
          return match ? `${match.display} (${match.value})` : value;
        },
      },
      {
        dataIndex: "zipCode",
        editable: true,
        inputType: "text",
        rules:
          selectedCountry === "US"
            ? [
                {
                  required: true,
                  pattern: /^\d{5}(-\d{4})?$/,
                  message: "Must be a valid zip code",
                },
              ]
            : [
                {
                  required: true,
                  message: "Enter zip/postal code",
                },
              ],
        inputProps: {
          placeholder: selectedCountry === "CA" ? "A1A 1A1" : undefined,
        },
        title: "Zip/Postal Code",
      },
    ],
    [addressTypeOptions, stateOptions, isStateSelect, selectedCountry, constants],
  );

  return (
    <TableFormList
      loading={isLoading}
      defaultValues={{ type: "mail", country: "US" }}
      columns={columns}
      data={forwardingEntityAddresses}
      onEdit={(record) => setSelectedCountry(record.country ?? "US")}
      onDelete={(address) =>
        deleteForwardingEntityAddress({
          forwardingEntityId,
          address,
        })
      }
      onSave={(address) => {
        const action = address.id ? updateForwardingEntityAddress : createForwardingEntityAddress;
        return action({
          forwardingEntityId,
          address,
        });
      }}
    />
  );
}
